import styled from '@emotion/styled';
import { rgba } from 'polished';
import type { RefObject } from 'react';
import React, { createRef, useEffect, useRef } from 'react';
import { animated, useSpring } from 'react-spring';
import { MobileDropdown } from '../../components/form';
import { useMedia } from '../../hooks/useMedia';
import { theme } from '../../styles';
import { device } from '../../styles/devices';
import { typography } from '../../styles/typography';

const ButtonGroup = styled.div<{
  variant?: 'underline' | 'block';
  tabSpacing: string;
}>`
  position: relative;
  width: 100%;
  display: flex;

  & > * + *:not(:last-child) {
    margin-left: ${({ tabSpacing }) => tabSpacing};
  }

  @media ${device.tablet} {
    border-bottom: ${({ variant, theme }) =>
      variant === 'block'
        ? '0px;'
        : `1px solid ${rgba(theme.colors.foreground, 0.3)}`};
    flex-direction: row;
  }
`;

const TabHost = styled.div``;

const Tab = styled.button<{
  selected: boolean;
  block: boolean;
  fontVariant?: 'secondary' | 'primary' | 'small';
  lineColor?: 'foreground' | 'secondary';
}>`
  padding-bottom: 26px;
  text-align: left;
  display: flex;
  align-items: center;

  ${typography.h5};
  ${({ block, fontVariant }) =>
    (block || fontVariant === 'small') && typography.bodySmall};
  ${({ fontVariant }) => fontVariant === 'secondary' && typography.body};
  ${({ selected, fontVariant }) =>
    !selected && fontVariant === 'secondary' && 'opacity: 0.6;'}
  ${({ selected, fontVariant }) =>
    selected && fontVariant === 'small' && 'font-weight: 500;'}
  ${({ fontVariant }) => fontVariant === 'small' && 'padding-bottom: 16px'};

  ${(props) => {
    if (props.block) {
      return `color: ${props.selected ? theme.colors.primary : 'white'};
      background: ${props.selected ? 'white' : 'rgb(30, 105, 176)'};
      justify-content: center;
      padding: 11px 10px 8px 10px;
      font-weight: bold;
      min-height: auto;
      width: 100%;
    `;
    }
  }};

  cursor: pointer;

  @media ${device.tablet} {
    margin-top: 0;
  }

  & > svg {
    margin-right: 10px;
  }
`;

const Underline = styled(animated.div)<{
  lineColor: 'foreground' | 'secondary';
}>`
  width: 100%;
  height: 4px;
  background: ${({ theme, lineColor }) => theme.colors[lineColor]};
  position: absolute;
  bottom: -1px;
  left: 0;
`;

interface ITabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

export interface IProps extends React.ComponentProps<'div'> {
  items: ITabItem[];
  selectedItemId: string;
  onSelectTab: (id: string) => void;
  mobileLabel?: string;
  mobileInset?: boolean;
  className?: string;
  mobileVariant?: 'tabs' | 'dropdown';
  fontVariant?: 'secondary' | 'primary' | 'small';
  variant?: 'underline' | 'block';
  lineColor?: 'foreground' | 'secondary';
  tabSpacing?: string;
}

export function SelectorTabs({
  items,
  selectedItemId,
  onSelectTab,
  mobileLabel,
  mobileInset,
  mobileVariant = 'dropdown',
  variant = 'underline',
  fontVariant = 'primary',
  lineColor = 'foreground',
  tabSpacing = '24px',
  ...rest
}: IProps) {
  const [elRefs, setElRefs] = React.useState<
    (HTMLButtonElement | RefObject<HTMLButtonElement>)[]
  >([]);
  const isMobile = useMedia('sm');

  const ref = useRef<HTMLDivElement>(null);
  const [underline, spring] = useSpring(() => ({
    x: 0,
    width: 0,
  }));

  React.useEffect(() => {
    // add or remove refs
    setElRefs((elRefs) =>
      Array(items.length)
        .fill(null)
        .map((_, i) => elRefs[i] || createRef<HTMLButtonElement>()),
    );
  }, [items.length]);

  const selectedRef = React.useMemo(() => {
    return elRefs
      ? elRefs[items.findIndex((x) => x.id === selectedItemId)]
      : null;
  }, [elRefs, selectedItemId, items]);

  const reset = React.useCallback(
    ({ immediate = false }: { immediate: boolean }) => {
      if (ref.current) {
        const host = ref.current;
        // const activeIndex = items.findIndex((x) => x.id === selectedItemId);
        const item = selectedRef;
        const hostRect = host.getBoundingClientRect();
        if (item) {
          const itemRect = (item as any).getBoundingClientRect();
          spring.start({
            x: itemRect.x - hostRect.x,
            width: itemRect.width,
            immediate,
          });
        } else {
          spring.start({ width: 0 });
        }
      }
    },
    [selectedRef, spring],
  );

  const onMouseOver = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (ref.current) {
        const item = e.currentTarget.getBoundingClientRect();
        const host = ref.current.getBoundingClientRect();
        spring.start({
          x: item.x - host.x,
          width: item.width,
        });
      }
    },
    [spring],
  );

  const onMouseOut = () => {
    reset({ immediate: false });
  };

  useEffect(() => {
    reset({ immediate: true });
  }, [reset, selectedRef]);

  return (
    <ButtonGroup ref={ref} tabSpacing={tabSpacing} {...rest}>
      {!isMobile || variant === 'block' || mobileVariant === 'tabs' ? (
        <>
          {items.map((x, index) => (
            <TabHost key={x.id}>
              <Tab
                selected={x.id === selectedItemId}
                onClick={(e) => {
                  onSelectTab(x.id);
                  return onMouseOver(e);
                }}
                onMouseOver={variant !== 'block' ? onMouseOver : undefined}
                onMouseOut={variant !== 'block' ? onMouseOut : undefined}
                block={variant === 'block'}
                ref={(ref) => {
                  if (ref) elRefs[index] = ref;
                }}
                fontVariant={fontVariant}
              >
                {x.icon}
                {x.label}
              </Tab>
            </TabHost>
          ))}
          {variant === 'underline' && (
            <Underline style={underline} lineColor={lineColor} />
          )}
        </>
      ) : (
        <MobileDropdown
          inset={mobileInset}
          bordered={!mobileInset}
          dialogTitle={mobileLabel ?? ''}
          options={items.map((i) => ({ value: i.id, label: i.label }))}
          value={selectedItemId}
          onChange={(val) => onSelectTab(val)}
        />
      )}
    </ButtonGroup>
  );
}
